import React, { useEffect, useRef, useCallback } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useField } from '@unform/core';

interface Option {
  value: string;
  label: string;
}

interface AutocompleteSelectProps {
  name: string;
  label?: string;
  options: Option[];
  placeholder?: string;
  required?: boolean;
  onChange?: (value: string) => void; // ⭐ essa linha
}

const AutocompleteSelect: React.FC<AutocompleteSelectProps> = ({
  name,
  label,
  options,
  placeholder,
  required,
  onChange,
}) => {
  const { fieldName, registerField, defaultValue, error } = useField(name);
  const selectedRef = useRef<Option | null>(
    options.find(o => o.value === defaultValue) ?? null,
  );

  useEffect(() => {
    registerField({
      name: fieldName,
      getValue: () => selectedRef.current?.value ?? '',
      setValue: (_ref, value: string) => {
        selectedRef.current = options.find(o => o.value === value) ?? null;
      },
      clearValue: () => {
        selectedRef.current = null;
      },
    });
  }, [fieldName, registerField, options]);

  return (
    <div className="autocomplete-wrapper">
      {label && (
        <p className="highlight-label">
          {label}
          {required && <span className="asterisk"> *</span>}
        </p>
      )}

      <Autocomplete
        disableClearable // ⭐ remove o X
        options={options}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        defaultValue={selectedRef.current}
        onChange={(_event, value) => {
          selectedRef.current = value;
          onChange?.(value?.value ?? '');
        }}
        noOptionsText="Nenhum resultado"
        renderInput={params => (
          <TextField
            {...params}
            placeholder={placeholder}
            error={!!error}
            helperText={error}
            variant="outlined"
            size="small"
          />
        )}
        slotProps={{
          popper: { sx: { zIndex: 9999 } },
        }}
      />
    </div>
  );
};

export default AutocompleteSelect;
